/**
 * Image Zoom Modal Content Loader
 *
 * Full-screen lightbox with pinch-to-zoom, pan, swipe navigation, and thumbnail navigation.
 * Optimized for mobile touch interactions.
 */

import { initSwipeGallery } from '../../interactions/swipe-gallery.js';

/**
 * Image zoom modal loader
 * @param {HTMLElement} contentEl - The modal content container
 * @param {Object} options - Options including images array, currentIndex
 * @param {AbortController} abortController - For cleanup
 * @param {Function} getDialog - Function to get dialog element
 */
export function imageZoomModalLoader(
  contentEl,
  { images = [], currentIndex = 0 } = {},
  abortController,
  getDialog
) {
  const dialog = getDialog();

  // Add zoom-specific class to dialog for styling
  dialog.classList.add('modal-zoom');

  // Build thumbnails HTML
  const thumbnailsHtml = images
    .map(
      (img, i) =>
        `<button class="zoom-thumb${i === currentIndex ? ' active' : ''}" data-index="${i}" aria-label="View image ${i + 1}">
        <img src="${img.thumb}" alt="" loading="lazy">
      </button>`
    )
    .join('');

  // Get current image safely
  const currentImage = images[currentIndex] || images[0] || { full: '', alt: 'Product image' };

  // Render content
  contentEl.innerHTML = `
    <div class="zoom-container" aria-label="Zoomable product image">
      <img 
        class="zoom-image" 
        src="${currentImage.full}" 
        alt="${currentImage.alt}"
        draggable="false"
      >
      <div class="zoom-preview"></div>
    </div>
    ${currentImage.alt ? `<div class="zoom-caption">${currentImage.alt}</div>` : ''}
    ${images.length > 1 ? `<div class="zoom-thumbs">${thumbnailsHtml}</div>` : ''}
    <div class="zoom-hint">Pinch or scroll to zoom</div>
  `;

  // Get elements
  const container = contentEl.querySelector('.zoom-container');
  const img = contentEl.querySelector('.zoom-image');
  const preview = contentEl.querySelector('.zoom-preview');
  const caption = contentEl.querySelector('.zoom-caption');
  const thumbs = contentEl.querySelectorAll('.zoom-thumb');

  // Style preview container for swipe animation
  preview.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0;
    will-change: transform, opacity;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  // State
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let isPanning = false;
  let startX = 0;
  let startY = 0;
  let lastTranslateX = 0;
  let lastTranslateY = 0;
  let currentIdx = currentIndex;

  // Pinch zoom state
  let initialPinchDistance = 0;
  let initialScale = 1;
  let activePointers = new Map();

  // Double tap state
  let lastTapTime = 0;

  // Constants
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;

  const { signal } = abortController;

  /**
   * Apply transform to image
   */
  function applyTransform() {
    img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
  }

  /**
   * Constrain pan within bounds
   */
  function constrainPan() {
    if (scale <= 1) {
      translateX = 0;
      translateY = 0;
      return;
    }

    const rect = container.getBoundingClientRect();
    const maxX = Math.max(0, (rect.width * scale - rect.width) / 2);
    const maxY = Math.max(0, (rect.height * scale - rect.height) / 2);

    translateX = Math.max(-maxX, Math.min(maxX, translateX));
    translateY = Math.max(-maxY, Math.min(maxY, translateY));
  }

  /**
   * Reset zoom and pan
   */
  function resetZoom() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    img.style.transition = 'transform 0.3s ease';
    applyTransform();
    setTimeout(() => {
      img.style.transition = '';
    }, 300);
  }

  /**
   * Zoom to point
   */
  function zoomToPoint(newScale, clientX, clientY) {
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left - rect.width / 2;
    const y = clientY - rect.top - rect.height / 2;

    // Adjust translate to zoom toward point
    const scaleDiff = newScale / scale;
    translateX = x - (x - translateX) * scaleDiff;
    translateY = y - (y - translateY) * scaleDiff;

    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    constrainPan();
    applyTransform();
  }

  /**
   * Get distance between two pointers
   */
  function getPointerDistance(pointers) {
    const points = Array.from(pointers.values());
    if (points.length < 2) return 0;
    const dx = points[1].x - points[0].x;
    const dy = points[1].y - points[0].y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Get center point between pointers
   */
  function getPointerCenter(pointers) {
    const points = Array.from(pointers.values());
    if (points.length < 2) return points[0] || { x: 0, y: 0 };
    return {
      x: (points[0].x + points[1].x) / 2,
      y: (points[0].y + points[1].y) / 2,
    };
  }

  /**
   * Handle pointer down
   */
  function onPointerDown(e) {
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size === 1) {
      // Single pointer - check if we should pan or let swipe handle it
      if (scale > 1) {
        // Start pan when zoomed
        isPanning = true;
        startX = e.clientX;
        startY = e.clientY;
        lastTranslateX = translateX;
        lastTranslateY = translateY;
        container.setPointerCapture(e.pointerId);
      }
      // When not zoomed, let swipe gallery handle it (don't capture)
    } else if (activePointers.size === 2) {
      // Two pointers - start pinch
      isPanning = false;
      initialPinchDistance = getPointerDistance(activePointers);
      initialScale = scale;
    }
  }

  /**
   * Handle pointer move
   */
  function onPointerMove(e) {
    if (!activePointers.has(e.pointerId)) return;

    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size === 2) {
      // Pinch zoom
      const distance = getPointerDistance(activePointers);
      const center = getPointerCenter(activePointers);
      const newScale = initialScale * (distance / initialPinchDistance);
      zoomToPoint(newScale, center.x, center.y);
    } else if (isPanning && scale > 1) {
      // Pan (only when zoomed)
      translateX = lastTranslateX + (e.clientX - startX);
      translateY = lastTranslateY + (e.clientY - startY);
      constrainPan();
      applyTransform();
    }
  }

  /**
   * Handle pointer up
   */
  function onPointerUp(e) {
    activePointers.delete(e.pointerId);

    if (activePointers.size === 0) {
      isPanning = false;

      // Check for double tap to toggle zoom
      const now = Date.now();
      if (now - lastTapTime < 300) {
        if (scale > 1) {
          // Double tap - reset zoom
          resetZoom();
        } else {
          // Double tap - zoom in
          img.style.transition = 'transform 0.3s ease';
          zoomToPoint(2.5, e.clientX, e.clientY);
          setTimeout(() => {
            img.style.transition = '';
          }, 300);
        }
        lastTapTime = 0;
      } else {
        lastTapTime = now;
      }
    } else if (activePointers.size === 1) {
      // Transition from pinch to pan
      const pointer = Array.from(activePointers.values())[0];
      startX = pointer.x;
      startY = pointer.y;
      lastTranslateX = translateX;
      lastTranslateY = translateY;
      isPanning = true;
    }
  }

  /**
   * Handle wheel zoom
   */
  function onWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoomToPoint(scale * delta, e.clientX, e.clientY);
  }

  /**
   * Update caption text
   */
  function updateCaption(text) {
    if (caption) {
      caption.textContent = text || '';
      caption.style.display = text ? '' : 'none';
    }
  }

  /**
   * Switch to image at index
   */
  function switchImage(index) {
    if (index < 0 || index >= images.length) return;

    // Reset zoom
    resetZoom();

    // Show loading state
    img.style.opacity = '0.4';
    container.classList.add('loading');

    // Update image
    const newSrc = images[index].full;
    img.alt = images[index].alt || 'Product image';
    currentIdx = index;

    // Update caption
    updateCaption(images[index].alt);

    // Update active thumb
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
    });

    // Load and reveal
    const tempImg = new Image();
    tempImg.onload = () => {
      img.src = newSrc;
      img.style.opacity = '1';
      container.classList.remove('loading');
    };
    tempImg.onerror = () => {
      img.src = newSrc;
      img.style.opacity = '1';
      container.classList.remove('loading');
    };
    tempImg.src = newSrc;
  }

  // Attach event listeners
  container.addEventListener('pointerdown', onPointerDown, { signal });
  container.addEventListener('pointermove', onPointerMove, { signal });
  container.addEventListener('pointerup', onPointerUp, { signal });
  container.addEventListener('pointercancel', onPointerUp, { signal });
  container.addEventListener('wheel', onWheel, { passive: false, signal });

  // Prevent context menu on long press
  container.addEventListener('contextmenu', (e) => e.preventDefault(), { signal });

  // Thumbnail click handlers
  thumbs.forEach((thumb) => {
    thumb.addEventListener(
      'click',
      () => {
        const index = parseInt(thumb.dataset.index, 10);
        switchImage(index);
      },
      { signal }
    );
  });

  // Initialize swipe gallery for image navigation (only when multiple images)
  // Use attachListeners: false so we can conditionally forward events only when not zoomed
  let swipeController = null;
  if (images.length > 1) {
    swipeController = initSwipeGallery({
      container,
      content: img,
      preview,
      getTotal: () => images.length,
      getCurrentIndex: () => currentIdx,
      getPreviewContent: (index) => {
        const previewImg = document.createElement('img');
        previewImg.src = images[index].full;
        previewImg.alt = images[index].alt || 'Product image';
        previewImg.style.cssText = 'max-width: 100%; max-height: 100%; object-fit: contain;';
        previewImg.draggable = false;
        return previewImg;
      },
      onNavigate: (newIndex) => {
        return new Promise((resolve) => {
          img.style.opacity = '0.4';
          container.classList.add('loading');

          const newSrc = images[newIndex].full;
          img.alt = images[newIndex].alt || 'Product image';
          currentIdx = newIndex;
          updateCaption(images[newIndex].alt);
          thumbs.forEach((thumb, i) => {
            thumb.classList.toggle('active', i === newIndex);
          });

          const tempImg = new Image();
          tempImg.onload = () => {
            img.src = newSrc;
            img.style.opacity = '1';
            container.classList.remove('loading');
            resolve();
          };
          tempImg.onerror = () => {
            img.src = newSrc;
            img.style.opacity = '1';
            container.classList.remove('loading');
            resolve();
          };
          tempImg.src = newSrc;
        });
      },
      wrap: true,
      attachListeners: false, // We handle listeners ourselves
    });

    // Forward touch events to swipe gallery only when not zoomed
    container.addEventListener(
      'touchstart',
      (e) => {
        if (scale <= 1) swipeController.handleTouchStart(e);
      },
      { passive: true, signal }
    );
    container.addEventListener(
      'touchmove',
      (e) => {
        if (scale <= 1) swipeController.handleTouchMove(e);
      },
      { passive: false, signal }
    );
    container.addEventListener(
      'touchend',
      () => {
        if (scale <= 1) swipeController.handleTouchEnd();
      },
      { passive: true, signal }
    );
    container.addEventListener(
      'touchcancel',
      () => {
        if (scale <= 1) swipeController.handleTouchCancel();
      },
      { passive: true, signal }
    );
  }

  // Keyboard navigation
  dialog.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'ArrowLeft') {
        switchImage(currentIdx - 1 >= 0 ? currentIdx - 1 : images.length - 1);
      } else if (e.key === 'ArrowRight') {
        switchImage(currentIdx + 1 < images.length ? currentIdx + 1 : 0);
      } else if (e.key === 'Escape') {
        window.ModalManager.close();
      }
    },
    { signal }
  );

  // Cleanup on modal close
  abortController.signal.addEventListener('abort', () => {
    dialog.classList.remove('modal-zoom');
    // Note: swipeController.destroy() not needed since we use attachListeners: false
    // and our event listeners are cleaned up via the abort signal
  });

  // Return undefined so ModalManager doesn't overwrite our innerHTML
  return undefined;
}
